import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { useFormContext } from "react-hook-form";
import TextField from "../../Form/TextField";
import LmsIntegrationDomainService from "../../../Domain/LmsIntegration/LmsIntegration.service";
import CustomCard from "../../UI/CustomCard/CustomCard";
import SearchField from "../../Form/SearchField";
import { LmsIntegrationSearch } from "./LmsIntegrationSearch";
import LmsConfigurationDomainService from "../../../Domain/LmsConfiguration/LmsConfiguration.service";
import NumberField from "../../Form/NumberField";
import CheckboxField from "../../Form/CheckboxField";
import InputField from "../../Form/InputField";
import CanvasConnectionHealthCard from "./CanvasConnectionHealthCard";
import LmsIntegrationService from "../../../Services/LmsIntegration.service";
// Canvas OAuth support (AV-19135)
const CANVAS_AUTH_METHOD_KEY = "authMethod";
const CANVAS_MANUAL_TOKEN_KEY = "securitytoken";
const CANVAS_CLIENT_ID_KEY = "clientId";
const CANVAS_OAUTH_ONLY_KEYS = ["clientId", "developerKey"];
// Canvas token-like fields masked in the UI: the OAuth client secret AND the manual security token.
// Masked = password input, never prefilled; a blank value on save keeps the stored one. (Only
// developerKey is additionally encrypted at rest — see LmsConfigurationController. The manual
// securitytoken is masked in the UI only; its storage is unchanged.)
const CANVAS_MASKED_KEYS = ["developerKey", "securitytoken"];
// Placeholder shown for a stored (masked) secret — long enough to read like a real token.
const CANVAS_MASK_PLACEHOLDER = "•".repeat(50);
const CANVAS_AUTH_OAUTH = "oauth";
const CANVAS_FIELD_LABELS = {
    clientId: "Client ID",
    developerKey: "Developer Key (Client Secret)",
};
const isCanvasIntegration = (lmsIntegration) => (lmsIntegration === null || lmsIntegration === void 0 ? void 0 : lmsIntegration.id) === LmsIntegrationDomainService.canvas.id;
export const sortFormDefaultConfigurationValues = (lmsConfigurations, selectedLmsIntegration) => {
    var _a;
    const lmsIntegrationConfigurations = lmsConfigurations.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id === selectedLmsIntegration.id &&
        !lmsConfiguration.advanced);
    const advancedGenericLmsIntegrationConfiguration = lmsConfigurations.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id ===
        LmsIntegrationDomainService.general.id);
    const advancedLmsIntegrationConfigurations = lmsConfigurations.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id === selectedLmsIntegration.id &&
        lmsConfiguration.advanced);
    return {
        lmsIntegrationConfiguration: Object.assign({}, lmsIntegrationConfigurations.reduce((acc, configuration) => (Object.assign(Object.assign({}, acc), { [configuration.key]: {
                // Never prefill a masked Canvas secret (OAuth client secret or manual security token).
                // Blank = "keep existing" on save, so unrelated edits don't wipe it and the value (or
                // ciphertext) never round-trips through the form. (AV-19135)
                stringValue: isCanvasIntegration(selectedLmsIntegration) &&
                    CANVAS_MASKED_KEYS.includes(configuration.key)
                    ? ""
                    : configuration.stringValue,
                longValue: configuration.longValue,
                booleanValue: configuration.booleanValue,
            } })), {})),
        advancedLmsIntegrationConfiguration: Object.assign({}, advancedLmsIntegrationConfigurations.reduce((acc, configuration) => (Object.assign(Object.assign({}, acc), { [configuration.key]: {
                stringValue: configuration.stringValue,
                longValue: configuration.longValue,
                booleanValue: configuration.booleanValue,
            } })), {})),
        advancedGenericLmsIntegrationConfiguration: Object.assign({}, advancedGenericLmsIntegrationConfiguration.reduce((acc, configuration) => (Object.assign(Object.assign({}, acc), { [configuration.key]: {
                stringValue: configuration.stringValue,
                longValue: configuration.longValue,
                booleanValue: configuration.booleanValue,
            } })), {})),
        lmsIntegration: (_a = lmsIntegrationConfigurations.find((a) => a.lmsIntegration.id === selectedLmsIntegration.id)) === null || _a === void 0 ? void 0 : _a.lmsIntegration,
    };
};
export const getLmsIntegrationConfigurationSaveRequest = (formState, selectedLmsIntegration) => {
    const lmsIntegration = Object.entries(Object.assign(Object.assign({}, formState.lmsIntegrationConfiguration), formState.advancedLmsIntegrationConfiguration)).map(([key, value]) => ({
        key,
        stringValue: value.stringValue,
        longValue: value.longValue,
        booleanValue: value.booleanValue,
        lmsIntegrationId: selectedLmsIntegration.id,
    }));
    const genericLmsIntegrationConfiguration = Object.entries(Object.assign({}, formState.advancedGenericLmsIntegrationConfiguration)).map(([key, value]) => ({
        key,
        stringValue: value.stringValue,
        longValue: value.longValue,
        booleanValue: value.booleanValue,
        lmsIntegrationId: LmsIntegrationDomainService.general.id,
    }));
    return [...lmsIntegration, ...genericLmsIntegrationConfiguration];
};
export const LmsIntegrationTypeSelector = ({ setSelectedLmsIntegration, editDisabled, }) => {
    const { watch } = useFormContext();
    const lmsIntegration = watch("lmsIntegration");
    setSelectedLmsIntegration(lmsIntegration);
    return (_jsx(SearchField, { label: "Integration Type", name: "lmsIntegration", disabled: editDisabled, as: (provided) => (_jsx(LmsIntegrationSearch, Object.assign({ excludeIds: [
                LmsIntegrationDomainService.general.id,
                LmsIntegrationDomainService.blackboardBuildingBlock.id,
            ] }, provided))) }));
};
/**
 * Canvas auth-method dropdown (AV-19135). A native select registered via RHF v4 `register()` so the
 * stored value is reliably the literal string "manual"/"oauth" (the shared DeprecatedSelect mangles
 * simple values, and this project's react-hook-form v4 Controller has no `render` prop).
 */
const CanvasAuthMethodSelect = ({ name }) => {
    const { register } = useFormContext();
    return (_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: name, children: "Canvas Auth Method" }), _jsxs("select", { id: name, name: name, ref: register(), className: "form-control", children: [_jsx("option", { value: "manual", children: "Canvas - Manual Token" }), _jsx("option", { value: CANVAS_AUTH_OAUTH, children: "Canvas - OAuth" })] })] }));
};
/**
 * A Canvas text field that is rendered conditionally on authMethod (clientId, manual securitytoken).
 * Because it mounts only after authMethod resolves — i.e. AFTER the form applies its default values —
 * it misses the RHF reset. `defaultValue` handles the visible prefill; the mount effect also seeds
 * RHF's tracked state so watch/getValues/submit see the value. (AV-19135)
 */
const CanvasSeededTextField = ({ name, label, value, }) => {
    const { setValue } = useFormContext();
    React.useEffect(() => {
        if (value) {
            setValue(name, value);
        }
    }, [name, value, setValue]);
    return (_jsx(InputField, { name: name, label: label, type: "text", autoComplete: "off", defaultValue: value }));
};
const LmsConfigurationField = ({ name, lmsConfiguration, isCanvas, authMethod, configuredSecretKeys, }) => {
    var _a, _b;
    if (isCanvas) {
        const key = lmsConfiguration.key;
        // Render the auth-method discriminator as a dropdown.
        if (key === CANVAS_AUTH_METHOD_KEY) {
            return _jsx(CanvasAuthMethodSelect, { name: name + ".stringValue" });
        }
        // Manual token shows only for manual; OAuth credentials show only for OAuth.
        if (key === CANVAS_MANUAL_TOKEN_KEY && authMethod === CANVAS_AUTH_OAUTH) {
            return null;
        }
        if (CANVAS_OAUTH_ONLY_KEYS.includes(key) &&
            authMethod !== CANVAS_AUTH_OAUTH) {
            return null;
        }
        // Masked secrets: the OAuth client secret (developerKey) and the manual security token. Password
        // input with a "saved" indicator; the real value is never sent back — blank keeps the stored
        // value, a new value replaces it.
        if (CANVAS_MASKED_KEYS.includes(key)) {
            const isConfigured = configuredSecretKeys === null || configuredSecretKeys === void 0 ? void 0 : configuredSecretKeys.includes(key);
            return (_jsx(InputField, { name: name + ".stringValue", label: (_a = CANVAS_FIELD_LABELS[key]) !== null && _a !== void 0 ? _a : key, type: "password", 
                // "new-password" stops Firefox/Chrome from offering saved logins here (and from pairing the
                // Client ID field as the matching username). "off" alone is ignored on login-like forms.
                autoComplete: "new-password", placeholder: isConfigured ? CANVAS_MASK_PLACEHOLDER : undefined, helpText: isConfigured
                    ? "Saved — leave blank to keep the current value, or enter a new one to replace it."
                    : undefined }));
        }
        if (key === CANVAS_CLIENT_ID_KEY) {
            return (_jsx(CanvasSeededTextField, { name: name + ".stringValue", label: CANVAS_FIELD_LABELS.clientId, value: (_b = lmsConfiguration.stringValue) !== null && _b !== void 0 ? _b : "" }));
        }
    }
    switch (lmsConfiguration.fieldTypeId) {
        case LmsConfigurationDomainService.stringFieldTypeId:
            return (_jsx(TextField, { name: name + ".stringValue", label: lmsConfiguration.key }));
        case LmsConfigurationDomainService.longFieldTypeId:
            return (_jsx(NumberField, { name: name + ".longValue", label: lmsConfiguration.key }));
        case LmsConfigurationDomainService.booleanFieldTypeId:
            return (_jsx(CheckboxField, { name: name + ".booleanValue", label: lmsConfiguration.key }));
        default:
            return (_jsx(TextField, { name: name + ".stringValue", label: lmsConfiguration.key }));
    }
};
const LmsIntegrationConfigurationFields = ({ fieldsData, name, isCanvas, authMethod, configuredSecretKeys, }) => {
    return (_jsx(_Fragment, { children: fieldsData === null || fieldsData === void 0 ? void 0 : fieldsData.map((lmsConfiguration, index) => (_jsx(LmsConfigurationField, { name: name + "." + lmsConfiguration.key, lmsConfiguration: lmsConfiguration, isCanvas: isCanvas, authMethod: authMethod, configuredSecretKeys: configuredSecretKeys }, index + name + lmsConfiguration.key))) }));
};
/** Canvas OAuth authorize + connection-health panel, shown when Canvas + OAuth is selected. (AV-19135) */
const CanvasOAuthPanel = ({ isDeveloperKeySaved, }) => {
    var _a, _b, _c;
    const { watch } = useFormContext();
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState(null);
    const [authorized, setAuthorized] = React.useState(false);
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("canvasOAuth");
    const reason = params.get("reason");
    const panelRef = React.useRef(null);
    // When we return from the Canvas authorize flow (?canvasOAuth=success|error), the result alert
    // lives on this card, which is below the fold — scroll it into view so the user sees the outcome
    // instead of landing at the top. Deferred and preceded by a blur so a field that grabs focus on
    // mount (which would auto-scroll itself into view) doesn't cancel this scroll. (AV-19135)
    React.useEffect(() => {
        if (!flag) {
            return undefined;
        }
        const timer = window.setTimeout(() => {
            var _a;
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
        return () => window.clearTimeout(timer);
    }, [flag]);
    // Canvas returns raw OAuth error codes; translate the actionable ones. invalid_scope means the
    // developer key no longer grants a scope SS&E requests at authorize time (all-or-nothing). (AV-19135)
    const describeAuthError = (code) => {
        if (code === "invalid_scope") {
            return "Canvas authorization failed: the developer key is missing one or more scopes SS&E requires.";
        }
        return `Canvas authorization failed${code ? `: ${code}` : ""}.`;
    };
    React.useEffect(() => {
        LmsIntegrationService.getCanvasOAuthStatus()
            .then((status) => setAuthorized(!!(status === null || status === void 0 ? void 0 : status.authorized)))
            .catch(() => setAuthorized(false));
    }, []);
    // Read current field values via watch — reliable for nested names in this RHF v4 setup
    // (getValues here does not return them consistently).
    const host = (_a = watch("lmsIntegrationConfiguration.host.stringValue")) !== null && _a !== void 0 ? _a : "";
    const clientId = (_b = watch("lmsIntegrationConfiguration.clientId.stringValue")) !== null && _b !== void 0 ? _b : "";
    const developerKey = (_c = watch("lmsIntegrationConfiguration.developerKey.stringValue")) !== null && _c !== void 0 ? _c : "";
    // Fields required before authorizing. A blank Developer Key is fine when one is already saved
    // (blank = keep the stored secret), so it's only required on first setup. (AV-19135)
    const missingFields = [
        !host.trim() && "host",
        !clientId.trim() && CANVAS_FIELD_LABELS.clientId,
        !developerKey.trim() &&
            !isDeveloperKeySaved &&
            CANVAS_FIELD_LABELS.developerKey,
    ].filter((field) => !!field);
    const hasMissingFields = missingFields.length > 0;
    // Save first (the callback needs host + credentials to build the authorize URL and exchange the
    // code), then redirect to Canvas. Persists automatically — no separate save; does not enable.
    const handleCreateAccessToken = () => {
        if (hasMissingFields) {
            return;
        }
        setSaving(true);
        setSaveError(null);
        LmsIntegrationService.saveCanvasOAuthCredentials(host, clientId, developerKey)
            .then(() => {
            window.location.href =
                LmsIntegrationService.getCanvasOAuthAuthorizeUrl();
        })
            .catch((e) => {
            var _a;
            setSaveError((_a = e === null || e === void 0 ? void 0 : e.message) !== null && _a !== void 0 ? _a : "Could not save the Canvas credentials.");
            setSaving(false);
        });
    };
    let accessTokenButtonLabel = "Create Access Token";
    if (saving) {
        accessTokenButtonLabel = "Saving…";
    }
    else if (authorized) {
        accessTokenButtonLabel = "Re-authorize";
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: panelRef, style: { scrollMarginTop: 120 }, children: _jsx(CustomCard, { title: "Canvas OAuth Authorization", className: "mb-3", children: _jsxs("div", { className: "card-body", children: [flag === "success" && (_jsx("div", { className: "alert alert-success", children: "Canvas authorization succeeded \u2014 an access token was acquired." })), flag === "error" && (_jsx("div", { className: "alert alert-danger", children: describeAuthError(reason) })), saveError && _jsx("div", { className: "alert alert-danger", children: saveError }), authorized && (_jsx("div", { className: "alert alert-info", children: "An access token has already been generated for this Canvas developer key. Re-authorize only to replace it (for example after it is revoked or the credentials change)." })), _jsxs("p", { className: "text-muted", children: ["Enter the Client ID and Developer Key above, then", " ", authorized ? "Re-authorize" : "Create Access Token", " \u2014 this saves the credentials and sends you to Canvas to authorize."] }), _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleCreateAccessToken, disabled: saving || hasMissingFields, "aria-describedby": hasMissingFields ? "canvas-oauth-missing-fields" : undefined, children: accessTokenButtonLabel }), hasMissingFields && (_jsxs("small", { id: "canvas-oauth-missing-fields", className: "form-text text-danger", children: ["Required before authorizing: ", missingFields.join(", "), "."] }))] }) }) }), _jsx(CanvasConnectionHealthCard, {})] }));
};
const LmsIntegrationConfigurationFormFields = ({ data, lmsIntegration, }) => {
    const lmsIntegrationConfigurations = data.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id !==
        LmsIntegrationDomainService.general.id && !lmsConfiguration.advanced);
    const advancedGenericLmsIntegrationConfiguration = data.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id ===
        LmsIntegrationDomainService.general.id);
    const advancedLmsIntegrationConfigurations = data.filter((lmsConfiguration) => lmsConfiguration.lmsIntegration.id !==
        LmsIntegrationDomainService.general.id && lmsConfiguration.advanced);
    const { watch } = useFormContext();
    const isCanvas = isCanvasIntegration(lmsIntegration);
    const authMethod = watch("lmsIntegrationConfiguration." + CANVAS_AUTH_METHOD_KEY + ".stringValue");
    // For Canvas, show the auth-method dropdown right after the "enabled" checkbox (stable sort keeps
    // the remaining fields in their original order).
    const mainFields = isCanvas
        ? [...lmsIntegrationConfigurations].sort((a, b) => {
            const rank = (key) => {
                if (key === "enabled")
                    return 0;
                if (key === CANVAS_AUTH_METHOD_KEY)
                    return 1;
                return 2;
            };
            return rank(a.key) - rank(b.key);
        })
        : lmsIntegrationConfigurations;
    // Which Canvas secrets already have a stored value (so the field can show a "saved" indicator
    // without exposing the value).
    const configuredSecretKeys = isCanvas
        ? data
            .filter((c) => c.lmsIntegration.id === LmsIntegrationDomainService.canvas.id &&
            CANVAS_MASKED_KEYS.includes(c.key) &&
            !!c.stringValue)
            .map((c) => c.key)
        : [];
    return (_jsxs(_Fragment, { children: [_jsx(CustomCard, { title: lmsIntegration.displayName + " Configuration", className: "mb-3", children: _jsx("div", { className: "card-body", children: _jsx(LmsIntegrationConfigurationFields, { fieldsData: mainFields, name: "lmsIntegrationConfiguration", isCanvas: isCanvas, authMethod: authMethod, configuredSecretKeys: configuredSecretKeys }) }) }), isCanvas && authMethod === CANVAS_AUTH_OAUTH && (_jsx(CanvasOAuthPanel, { isDeveloperKeySaved: configuredSecretKeys.includes("developerKey") })), _jsx(CustomCard, { title: "Advanced " + lmsIntegration.displayName + " Configuration", className: "mb-3", children: _jsx("div", { className: "card-body", children: _jsx(LmsIntegrationConfigurationFields, { fieldsData: advancedLmsIntegrationConfigurations, name: "advancedLmsIntegrationConfiguration" }) }) }), _jsx(CustomCard, { title: "Advanced General Configuration", className: "mb-3", children: _jsx("div", { className: "card-body", children: _jsx(LmsIntegrationConfigurationFields, { fieldsData: advancedGenericLmsIntegrationConfiguration, name: "advancedGenericLmsIntegrationConfiguration" }) }) })] }));
};
const LmsIntegrationForm = ({ data, lmsIntegration, }) => {
    return (_jsx(_Fragment, { children: _jsx(LmsIntegrationConfigurationFormFields, { data: data, lmsIntegration: lmsIntegration }) }));
};
export default LmsIntegrationForm;
